/**
 * Custom schema for bold label text
 * Displays label (before colon) in bold, value (after colon) in normal weight
 * Example: "Warengruppe: " -> "Warengruppe:" bold, rest normal
 */

import { PDF_BODY_SIZE } from "./template";

/**
 * Draw a label with bold text before colon, normal text after
 */
async function drawBoldLabel(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: {
    position: { x: number; y: number };
    width: number;
    height: number;
  };
  value: string;
}): Promise<void> {
  const { rgb, StandardFonts } = await import("pdf-lib");
  const { value } = arg;

  if (!value || value.trim().length === 0) {
    return;
  }

  const { x, y } = arg.schema.position;
  const { width, height } = arg.schema;
  const pageHeight = arg.page.getHeight();

  // Convert mm to points
  const MM_TO_PT = 2.83465;
  const startX = x * MM_TO_PT;
  const startY = pageHeight - y * MM_TO_PT; // Top of schema area (PDF coordinates)

  const font = await arg.pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await arg.pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontSize = PDF_BODY_SIZE; // Use central font size definition

  // Split text at colon
  const colonIndex = value.indexOf(":");
  const labelText =
    colonIndex >= 0 ? value.substring(0, colonIndex + 1) : value;
  const valueText = colonIndex >= 0 ? value.substring(colonIndex + 1) : "";

  // Calculate positions
  const labelWidth = fontBold.widthOfTextAtSize(labelText, fontSize);
  const valueWidth = valueText
    ? font.widthOfTextAtSize(valueText, fontSize)
    : 0;
  const totalWidth = labelWidth + valueWidth;

  // Center text horizontally
  const textX = startX + (width * MM_TO_PT - totalWidth) / 2;
  const textY = startY - (height * MM_TO_PT) / 2 - fontSize / 2;

  // Draw bold label
  if (labelText) {
    arg.page.drawText(labelText, {
      x: textX,
      y: textY,
      size: fontSize,
      font: fontBold,
      color: rgb(0, 0, 0),
    });
  }

  // Draw normal value
  if (valueText) {
    arg.page.drawText(valueText, {
      x: textX + labelWidth,
      y: textY,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }
}

export const boldLabelSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.fontSize = "13.5px";
    div.style.color = "#000";

    // Split text at colon
    const colonIndex = (arg.value || "").indexOf(":");
    const labelText =
      colonIndex >= 0 ? arg.value.substring(0, colonIndex + 1) : arg.value;
    const valueText =
      colonIndex >= 0 ? arg.value.substring(colonIndex + 1) : "";

    const labelSpan = document.createElement("span");
    labelSpan.style.fontWeight = "bold";
    labelSpan.textContent = labelText;

    const valueSpan = document.createElement("span");
    valueSpan.textContent = valueText;

    div.appendChild(labelSpan);
    div.appendChild(valueSpan);
    arg.rootElement.appendChild(div);
  },

  pdf: drawBoldLabel,

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "boldLabel",
      type: "boldLabel",
      position: { x: 0, y: 0 },
      width: 48,
      height: 5,
    },
    defaultValue: "",
  },
};

export default boldLabelSchema;
